import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { getUserInfo, parseCode } from '@/api/api';
let timer: NodeJS.Timeout;

type AuthStep = 'verifying' | 'parsing' | 'getting-userinfo' | 'success' | 'error';
const PREFIX = import.meta.env.VITE_PROTOCOL_SCHEME;

const DesktopAuthCallback = ({ app }: { app?: string }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<AuthStep>('verifying');
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(20);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    handleDesktopAuth();
  }, []);

  useEffect(() => {
    console.log('Countdown:', countdown, step);
    if (step === 'success' && countdown > 0) {
      console.log(`Countdown: ${countdown}s remaining`);
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (step === 'success' && countdown === 0) {
      console.log('Countdown finished, redirecting...');
      if (app === 'panasonic') {
        window.close();
      } else {
        window.location.href = import.meta.env.VITE_FELO_SUBTITLES_URL;
      }
      // window.location.href = import.meta.env.VITE_FELO_SUBTITLES_URL;
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [step, countdown, app]);

  const triggerDesktopProtocol = (url: string) => {
    if (iframeRef.current) {
      iframeRef.current.src = url;
    }
  };

  const handleDesktopAuth = async () => {
    try {
      console.log('Desktop auth callback started');
      console.log('Current URL:', window.location.href);

      // 第一步：验证现有登录状态
      setStep('verifying');
      await new Promise((resolve) => setTimeout(resolve, 500)); // 添加延迟显示加载状态

      let token = Cookies.get(import.meta.env.VITE_COOKIE_SUFFIX as string);
      console.log('Existing token found:', !!token);

      // 如果URL中有hash，尝试解析获取新的token
      if (window.location.hash) {
        console.log('Hash found in URL, parsing code...');
        setStep('parsing');
        await new Promise((resolve) => setTimeout(resolve, 500));

        const hashWithoutSymbol = window.location.hash.slice(1);
        console.log('Code from hash:', hashWithoutSymbol);

        try {
          const { data: { data: newToken } } = await parseCode(hashWithoutSymbol);
          console.log('Token parsed successfully from code');

          // 设置token到cookie
          Cookies.set(import.meta.env.VITE_COOKIE_SUFFIX as string, newToken, {
            expires: 30,
            domain: import.meta.env.VITE_DOMAIN,
          });

          // 设置token到localStorage (for desktop client)
          localStorage.setItem('token', newToken);

          token = newToken;

          // 清理URL中的hash和isNew参数
          const searchParams = new URLSearchParams(window.location.search);
          const isNew = searchParams.get('isNew');
          if (isNew) {
            const currentUrl = window.location.href;
            let newUrl = currentUrl.split('&isNew=')[0];
            newUrl = newUrl.split('?isNew=')[0];
            newUrl = newUrl.split('#')[0]; // 移除hash部分
            window.history.replaceState(null, '', newUrl);
          }
        } catch (parseError) {
          console.error('Failed to parse code from hash:', parseError);
          throw new Error(t('desktop.auth.error.parse'));
        }
      }

      if (!token) {
        console.log('No token available, redirecting to login');
        // 未登录，重定向到登录页面
        const loginUrl = new URL(`${import.meta.env.VITE_FELO_API_URL}/login`);
        loginUrl.searchParams.append('app_id', 'subtitle');
        loginUrl.searchParams.append('app_source', 'desktop');
        loginUrl.searchParams.append('isNew', 'true'); // 标记为初次登录
        // 将 redirect_uri 放在 hash 中以避免 URL 过长导致 414 错误
        loginUrl.hash = `redirect_uri=${encodeURIComponent(window.location.href.split('#')[0])}`; // 移除原有hash
        window.location.href = loginUrl.toString();
        return;
      }

      // 第二步：获取用户信息
      console.log('Getting user info...');
      setStep('getting-userinfo');
      await new Promise((resolve) => setTimeout(resolve, 500));

      const { data } = await getUserInfo();

      if (data.status === 200) {
        console.log('User info retrieved successfully');

        // 构造回调URL
        const callbackUrl = `${PREFIX}://auth-callback?`
          + `token=${encodeURIComponent(token)}&`
          + 'status=SUCCESS';

        console.log('Calling desktop protocol:', callbackUrl);

        // 使用iframe调用自定义协议
        triggerDesktopProtocol(callbackUrl);

        console.log('Setting step to success and starting countdown...');
        setStep('success');
        setMessage(t('desktop.auth.success.desc'));
        setCountdown(20); // 重置倒计时

        // 确保倒计时立即开始
        console.log('Authentication successful, countdown started');
      } else {
        throw new Error(t('desktop.auth.error.userinfo', { message: data.message || 'Unknown error' }));
      }
    } catch (error) {
      console.error('Desktop auth error:', error);

      // 错误处理
      const errorMessage = error instanceof Error ? error.message : t('desktop.auth.error.generic', { message: 'Unknown error' });
      const callbackUrl = `${PREFIX}://auth-callback?`
        + 'status=ERROR&'
        + `message=${encodeURIComponent(errorMessage)}`;

      console.log('Calling desktop protocol with error:', callbackUrl);
      triggerDesktopProtocol(callbackUrl);

      setStep('error');
      setMessage(errorMessage);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'verifying': return t('desktop.auth.verifying');
      case 'parsing': return t('desktop.auth.parsing.code');
      case 'getting-userinfo': return t('desktop.auth.getting.userinfo');
      case 'success': return t('desktop.auth.success');
      case 'error': return t('desktop.auth.error');
      default: return '';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 'verifying': return t('desktop.auth.verifying.desc');
      case 'parsing': return t('desktop.auth.parsing.desc');
      case 'getting-userinfo': return t('desktop.auth.getting.desc');
      case 'success': return t('desktop.auth.success.close');
      case 'error': return message;
      default: return '';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Hidden iframe for protocol triggering */}
      <iframe
        ref={iframeRef}
        style={{ display: 'none' }}
        title="Desktop Protocol Trigger"
      />

      <div className="max-w-md w-full mx-4">
        {/* Main Card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-100 p-8 text-center">
          {/* Logo or Brand */}
          <div className="mb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">{t('desktop.auth.title')}</h1>
            <p className="text-gray-500 mt-2 text-sm">{t('desktop.auth.brand')}</p>
          </div>

          {/* Status Icon and Content */}
          {step !== 'error' && step !== 'success' && (
            <div className="space-y-6">
              <div className="w-12 h-12 mx-auto">
                <div className="w-12 h-12 border-3 border-gray-200 rounded-full"></div>
                <div className="absolute inset-0 w-12 h-12 border-3 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
              </div>
              <div className="space-y-3">
                <h2 className="text-xl font-semibold text-gray-800">{getStepTitle()}</h2>
                <p className="text-gray-600">{getStepDescription()}</p>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="space-y-6">
              <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>

              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-800">{getStepTitle()}</h2>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-700 font-medium">{getStepDescription()}</p>
                </div>
              </div>

              <div className="text-sm text-gray-600">
                {t('desktop.auth.returning')} ({countdown}s)
              </div>
            </div>
          )}

          {step === 'error' && (
            <div className="space-y-6">
              <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>

              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-800">{getStepTitle()}</h2>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm">{getStepDescription()}</p>
                </div>
              </div>

              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center justify-center font-medium rounded-lg transition-colors duration-150 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed select-none bg-gray-700 text-white hover:bg-gray-800 active:bg-gray-900 px-4 py-2.5 text-base gap-2.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="white" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>{t('desktop.auth.retry')}</span>
                </button>
                <button
                  onClick={() => window.close()}
                  className="inline-flex items-center justify-center font-medium rounded-lg transition-colors duration-150 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed select-none bg-black/[0.04] text-gray-700 hover:bg-black/[0.08] active:bg-black/[0.12] px-4 py-2.5 text-base gap-2.5"
                >
                  {t('desktop.auth.close')}
                </button>
              </div>
            </div>
          )}

          {/* Brand Footer */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="text-xs text-gray-400">{t('desktop.auth.brand')}</div>
            <div className="text-xs text-gray-300 mt-2">{t('desktop.auth.security')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopAuthCallback;